import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Flex,
  Heading,
  Spinner,
  Center,
  Image,
  Text,
  Box,
  Stack,
  Divider,
  Select,
  Input,
  IconButton,
  Avatar,
  Tooltip,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
} from "@chakra-ui/react";
import { DeleteIcon  } from "@chakra-ui/icons";
import { API_URL, CURRENCY } from "../../../constants";
import { getAuthToken } from "../../../helpers/helpers";
import defaultImage from "assets/img/meds.png";
import { useLocation } from "react-router-dom";

export default function ProfileHistory() {
  const location = useLocation();
  const profileId = location.state?.id; 

  const [groupedHistory, setGroupedHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]); 
  const [selectedProfileId, setSelectedProfileId] = useState(() => profileId || null);
  const [selectedDate, setSelectedDate] = useState("");
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      const token = getAuthToken();
      try {
        const userId = localStorage.getItem("UserID");
        const response = await axios.get(`${API_URL}/api/profiles/${userId}`, {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        });

        setProfiles(response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération des profils:", error);
      }
    };

    fetchProfiles();
  }, [profileId]); 

  useEffect(() => {
    fetchData();
  }, [selectedProfileId, selectedDate]); 

  const monthLabel = (monthKey) => {
    const [yearStr, monthStr] = String(monthKey).split("-");
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const date = new Date(year, monthIndex, 1);
    const raw = date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    if (!raw) return raw;
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const fetchData = async () => {
    setLoading(true);
    const token = getAuthToken();
    try {
      const response = await axios.get(`${API_URL}/api/cart`, {
        headers: {
          "x-access-token": token,
          Authorization: `Bearer ${token}`,
        },
      });

      let carts = response.data;

      if (selectedProfileId) {
        carts = carts.filter(
          (cart) =>
            cart.profileId?.["_id"] &&
            cart.profileId._id.toString() === selectedProfileId.toString()
        );
      }

      if (selectedDate) {
        carts = carts.filter(
          (cart) => new Date(cart.created_at).toISOString().split("T")[0] === selectedDate
        );
      }

      const dayMap = carts.reduce((acc, cart) => {
        const createdAt = new Date(cart.created_at);
        const isoDay = createdAt.toISOString().split("T")[0]; // YYYY-MM-DD
        const displayDay = `${String(createdAt.getDate()).padStart(2, "0")}/${String(
          createdAt.getMonth() + 1
        ).padStart(2, "0")}/${createdAt.getFullYear()}`;

        const items = cart.items.map((item) => ({
          _id: item._id,
          specialite: item.name,
          image: item.medicamentDetails?.photo || defaultImage,
          price: item.medicamentDetails?.prix || 0,
          date_peremption: item.medicamentDetails?.date_peremption,
          nearExpiryMonths: item.medicamentDetails?.nearExpiryMonths ?? item.nearExpiryMonths ?? 6,
          points: item.point || 0,
          profileName:
            !cart.profileId?.nom_operateur || cart.profileId.nom_operateur === "Unknown"
              ? "Opérateur inconnu"
              : cart.profileId.nom_operateur,
          profilePhoto: cart.profileId?.photo || "",
          cardId: cart._id,
        }));

        if (!acc[isoDay]) {
          acc[isoDay] = {
            isoDay,
            displayDay,
            items: [],
            totalPrice: 0,
            totalPoints: 0,
          };
        }

        acc[isoDay].items.push(...items);
        acc[isoDay].totalPrice += items.reduce((sum, it) => sum + Number(it.price), 0);
        acc[isoDay].totalPoints += items.reduce((sum, it) => sum + Number(it.points), 0);
        return acc;
      }, {});

      const days = Object.values(dayMap).sort((a, b) => (a.isoDay < b.isoDay ? 1 : -1));

      const monthMap = days.reduce((acc, day) => {
        const monthKey = day.isoDay.slice(0, 7); // YYYY-MM
        if (!acc[monthKey]) {
          acc[monthKey] = {
            monthKey,
            days: [],
            totalPrice: 0,
            totalPoints: 0,
          };
        }
        acc[monthKey].days.push(day);
        acc[monthKey].totalPrice += day.totalPrice;
        acc[monthKey].totalPoints += day.totalPoints;
        return acc;
      }, {});

      const months = Object.values(monthMap).sort((a, b) => (a.monthKey < b.monthKey ? 1 : -1));
      setGroupedHistory(months);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique:", error);
      setGroupedHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSelectedProfileId(null);
    setSelectedDate("");
  };

  const handleDelete = async (cartId, itemId, itemName) => {
    setDeleteItem({ cartId, itemId, itemName });
    setAlertOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    const { cartId, itemId } = deleteItem;
    const token = getAuthToken();
    try {
      const response = await axios.delete(
        `${API_URL}/api/cart/${cartId}/item/${itemId}`,
        {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        await fetchData();
      } else {
        console.error("Failed to delete item.");
      }
    } catch (error) {
      console.error("Error while deleting item:", error);
    } finally {
      setAlertOpen(false);
      setDeleteItem(null);
    }
  };

  const cancelDelete = () => {
    setAlertOpen(false);
    setDeleteItem(null);
  };

  return (
    <Flex flexDirection="column" py="30px" px="20px" mt={10}>
      <Flex justifyContent="space-between" alignItems="center" mb="20px">
        <Heading size="lg" color="blue.600">
          🛒 Historique des Ventes
        </Heading>
        <Flex>
          <Select
            w="300px"
            value={selectedProfileId || ""}
            onChange={(e) =>
              setSelectedProfileId(e.target.value ? e.target.value : null)
            }
          >
            <option value="">Tous les profils</option>
            {profiles.map((profile) => (
              <option key={profile._id} value={profile._id}>
                {profile.nom_operateur}
              </option>
            ))}
          </Select>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            ml={4}
          />
          <IconButton
            ml={4}
            aria-label="Reset filters"
            icon={<DeleteIcon />}
            onClick={resetFilters}
          />
        </Flex>
      </Flex>

      {loading ? (
        <Center h="50vh">
          <Spinner size="lg" color="blue.500" />
        </Center>
      ) : groupedHistory.length > 0 ? (
        <Accordion allowMultiple>
          {groupedHistory.map(({ monthKey, days, totalPrice, totalPoints }) => (
            <AccordionItem key={monthKey} border="none" mb="12px">
              <Box bg="white" boxShadow="md" borderRadius="10px">
                <AccordionButton px="16px" py="14px" _hover={{ bg: "gray.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="gray.700">
                      {monthLabel(monthKey)}
                    </Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      💰 Total: <strong>{Number(totalPrice).toFixed(2)} {CURRENCY.code}</strong> · 💊 Points: <strong>{totalPoints}</strong>
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px="16px" pb="16px">
                  {days.map(({ isoDay, displayDay, items, totalPrice: dayTotalPrice, totalPoints: dayTotalPoints }) => (
                    <Box key={isoDay} mt="14px">
                      <Heading size="sm" color="gray.700" mb="10px">
                        📅 {displayDay} · 💰 <strong>{Number(dayTotalPrice).toFixed(2)} {CURRENCY.code}</strong> · 💊 <strong>{dayTotalPoints}</strong>
                      </Heading>
                      {items.map((item, index) => {
                        const isApproachingExpiration = (() => {
                          if (!item.date_peremption) return false;
                          const expirationDate = new Date(item.date_peremption);
                          const now = new Date();
                          const months = item.nearExpiryMonths;
                          const thresholdFromNow = new Date();
                          thresholdFromNow.setMonth(now.getMonth() + months);
                          return expirationDate > now && expirationDate <= thresholdFromNow;
                        })();

                        return (
                          <Box
                            key={`${item._id}-${index}`}
                            p="15px"
                            borderRadius="10px"
                            bg="white"
                            boxShadow="sm"
                            mb="12px"
                            position="relative"
                          >
                            <Flex alignItems="center" justify="space-between">
                              <Flex alignItems="center">
                                <Image
                                  src={item.image}
                                  alt={item.specialite}
                                  boxSize="60px"
                                  borderRadius="full"
                                  mr="15px"
                                />
                                <Stack spacing={1} w="100%">
                                  <Text fontWeight="bold" fontSize="lg" color="blue.700">
                                    {item.specialite}
                                  </Text>
                                  <Text fontSize="sm" color="gray.600">
                                    💰 Prix : <strong>{item.price} {CURRENCY.code}</strong>
                                  </Text>
                                  <Tooltip
                                    label="Produit périmé"
                                    isDisabled={!isApproachingExpiration}
                                    hasArrow
                                    placement="top"
                                    bg="red.500"
                                  >
                                    <Text
                                      fontSize="sm"
                                      color={isApproachingExpiration ? "red" : "gray.500"}
                                    >
                                      ⏳ Expire le : {item.date_peremption || "N/A"}
                                    </Text>
                                  </Tooltip>
                                  <Flex align="center" mt={2}>
                                    <Avatar
                                      src={item.profilePhoto}
                                      name={item.profileName}
                                      size="sm"
                                      mr={2}
                                    />
                                    <Text
                                      fontSize="xs"
                                      fontWeight="bold"
                                      color="gray.600"
                                    >
                                      {item.profileName}
                                    </Text>
                                  </Flex>
                                </Stack>
                              </Flex>
                              <Tooltip label="Supprimer cette vente" hasArrow>
                                <IconButton
                                  aria-label="Supprimer la vente"
                                  icon={<DeleteIcon />}
                                  colorScheme="red"
                                  variant="ghost"
                                  borderRadius="full"
                                  _hover={{
                                    backgroundColor: "red.100",
                                  }}
                                  onClick={() =>
                                    handleDelete(item.cardId, item._id, item.specialite)
                                  }
                                />
                              </Tooltip>
                            </Flex>
                          </Box>
                        );
                      })}
                      <Divider mt="10px" />
                    </Box>
                  ))}
                </AccordionPanel>
              </Box>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <Text textAlign="center" fontSize="lg" color="gray.500">
          ❌ Aucun historique disponible
        </Text>
      )}
      <AlertDialog isOpen={isAlertOpen} onClose={cancelDelete}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmer la suppression
            </AlertDialogHeader>
            <AlertDialogBody>
              Êtes-vous sûr de vouloir supprimer cet article ?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={cancelDelete}>Annuler</Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}
